import {
  NeedsClusterRemovedPulledClusterPhotoDto,
  needsClusterRemovedPulledClusterPhotoValidationSchema,
  NeedsToBeMovedToStagingWarrantyCertPhotoDto,
  vehicleCheckInManufacturersLabelPhotoValidationSchema
} from 'dtos'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import { AddAPhoto, HideImage, QuestionMark } from '@mui/icons-material'
import {
  Dialog,
  DialogContent,
  Grid,
  DialogTitle,
  Button,
  TextField,
  DialogActions,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup
} from '@mui/material'
import { ExplanationAccordion, GreenText } from 'components'
import { Formik } from 'formik'
import { NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'

interface WarrantyCertPhotoDialogProps {
  initialValues: NeedsToBeMovedToStagingWarrantyCertPhotoDto
  isLoading: boolean
  onClose: () => void
  onDelete: () => void
  onSave: (values: NeedsToBeMovedToStagingWarrantyCertPhotoDto) => void
  open: boolean
  vehicleGuid: string // Used to preview image
}

export default function WarrantyCertPhotoDialog({
  initialValues,
  isLoading,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: WarrantyCertPhotoDialogProps) {
  const [src, setSrc] = useState<string>('')

  useEffect(() => {
    if (initialValues.warrantyCertPhotoFileName && vehicleGuid) {
      controllers.getNeedsToBeMovedToStagingPhotoBase64(vehicleGuid, initialValues.warrantyCertPhotoFileName).then(base64 => {
        setSrc(`data:image/${initialValues.warrantyCertPhotoFileName!.split('.').pop()};base64,${base64}`)
      })
    } else {
      setSrc('')
    }
  }, [initialValues.warrantyCertPhotoFileName, vehicleGuid])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Warranty Cert Photo Capture</DialogTitle>

                  <Grid item xs={12}>
                    <img
                      alt='Warranty Cert'
                      src={values.warrantyCertPhotoFile ? URL.createObjectURL(values.warrantyCertPhotoFile) : src}
                      style={{ width: '100%' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {errors.warrantyCertPhotoFile && (
                      <Typography color='error' variant='body2'>
                        {errors.warrantyCertPhotoFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='image/png, image/jpeg'
                      capture='environment'
                      hidden
                      id='fileInput'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('warrantyCertPhotoFile', e.target.files[0])
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<AddAPhoto />}
                      fullWidth
                      onClick={() => {
                        document.getElementById('fileInput')?.click()
                      }}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Warranty Cert Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1' gutterBottom>
                      Please select the locations that the vehicle is being moved to for this Outbound Trip. This will update
                      the “Parking Space” on the vehicle record.
                      <br />
                      <br />
                      Note: If “Other” is selected the text field becomes enabled and required.
                    </Typography>

                    <RadioGroup
                      name='parkingSpace'
                      value={
                        values.parkingSpace === 'Staging' || values.parkingSpace === 'Truck' || values.parkingSpace === ''
                          ? values.parkingSpace
                          : 'Other'
                      }
                      onChange={e => {
                        const selected = e.target.value
                        if (selected === 'Other') {
                          setFieldValue('parkingSpace', '')
                        } else {
                          setFieldValue('parkingSpace', selected)
                        }
                      }}
                    >
                      <FormControlLabel value='Staging' control={<Radio />} label='Staging' />
                      <FormControlLabel value='Truck' control={<Radio />} label='Truck' />
                      <FormControlLabel
                        value='Other'
                        control={<Radio checked={values.parkingSpace !== 'Staging' && values.parkingSpace !== 'Truck'} />}
                        label='Other'
                      />
                    </RadioGroup>

                    <TextField
                      sx={{ mt: 1 }}
                      fullWidth
                      label='Other'
                      name='parkingSpace'
                      value={values.parkingSpace}
                      onChange={handleChange}
                      disabled={values.parkingSpace === 'Staging' || values.parkingSpace === 'Truck'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Preview:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing
them to:

Take a new photo and upload it.

Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

Clear/delete the currently uploaded photo.

Remove it from the Photo Previewer.

**Parking Space Radio Buttons:**

Displays the parking space options as radio buttons and is
required. Users can only select 1 parking space.

If the users has selected the “Other” radio button option the
“Other” text field is enabled and required.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not
save any changes

**SAVE Button:**

Clicking this button will:

Save the uploaded photo to the database and close the
dialog

                      `}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  color='inherit'
                  disabled={isSubmitting || isLoading}
                  startIcon={<NavigateBeforeIcon />}
                  onClick={onClose}
                  variant='text'
                >
                  CANCEL
                </Button>

                <Button
                  color='primary'
                  disabled={isSubmitting || isLoading}
                  endIcon={<NavigateNextIcon />}
                  type='submit'
                  variant='text'
                >
                  SUBMIT
                </Button>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
